import api from "@/axiosInterceptor";

const DISTRIBUTION_PLOT_SAMPLES_PATH = "public/api/samples/qc/distribution-plot";

function normalizeSampleRows(payload) {
  const rows =
    payload?.data?.samples ??
    payload?.data?.Samples ??
    payload?.data?.data?.samples ??
    payload?.samples ??
    payload?.Samples ??
    (Array.isArray(payload?.data) ? payload.data : null);
  if (!Array.isArray(rows)) return [];
  return rows;
}

function rowToChip(row) {
  const mapped = mapDistributionPlotApiRowToCsvFields(row);
  if (!mapped) return { id: "", fluidSampleID: "" };
  return { id: mapped.sampleId, fluidSampleID: mapped.fluidSampleID };
}

/** Fields for CSV export (aligned with distribution-plot API sample objects). */
export function mapDistributionPlotApiRowToCsvFields(row) {
  if (!row || typeof row !== "object") return null;
  const sampleId =
    row.ID != null && row.ID !== ""
      ? String(row.ID)
      : row.id != null && row.id !== ""
        ? String(row.id)
        : "";
  const fluidSampleID =
    row.FluidSampleID != null && row.FluidSampleID !== ""
      ? String(row.FluidSampleID).trim()
      : row.fluidSampleID != null
        ? String(row.fluidSampleID).trim()
        : "";
  if (!sampleId && !fluidSampleID) return null;
  const asset =
    row.Asset != null ? String(row.Asset) : row.asset != null ? String(row.asset) : "";
  const field =
    row.Field != null ? String(row.Field) : row.field != null ? String(row.field) : "";
  const well = row.Well != null ? String(row.Well) : row.well != null ? String(row.well) : "";
  const loc = row.Location ?? row.location;
  const reservoirFromLoc =
    loc && typeof loc === "object"
      ? loc.Reservoir != null
        ? loc.Reservoir
        : loc.reservoir != null
          ? loc.reservoir
          : null
      : null;
  const reservoir =
    row.Reservoir != null
      ? String(row.Reservoir)
      : row.reservoir != null
        ? String(row.reservoir)
        : reservoirFromLoc != null
          ? String(reservoirFromLoc)
          : "";
  const plotCategory = plotMetricOrCategoryCell(row);
  return { sampleId, asset, field, well, reservoir, fluidSampleID, plotCategory };
}

/** Last CSV column: prefer numeric/chart metric (`Value`, etc.), then bin/category keys. */
function plotMetricOrCategoryCell(row) {
  const nested = row.Data ?? row.data ?? row.PlotData ?? row.plotData;
  const metric =
    row.Value ??
    row.value ??
    row.PlotValue ??
    row.plotValue ??
    row.DistributionPlotValue ??
    row.distributionPlotValue ??
    row.Measurement ??
    row.measurement ??
    row.Result ??
    row.result ??
    row.Metric ??
    row.metric ??
    (nested && typeof nested === "object"
      ? nested.Value ?? nested.value ?? nested.PlotValue ?? nested.plotValue
      : undefined);
  if (metric !== undefined && metric !== null) {
    if (typeof metric === "number" && Number.isFinite(metric)) return String(metric);
    const t = String(metric).trim();
    if (t !== "") return t;
  }
  if (row.PlotKey != null && String(row.PlotKey).trim() !== "") return String(row.PlotKey).trim();
  if (row.plotKey != null && String(row.plotKey).trim() !== "") return String(row.plotKey).trim();
  if (row.DistributionPlotKey != null && String(row.DistributionPlotKey).trim() !== "")
    return String(row.DistributionPlotKey).trim();
  if (row.Range != null && String(row.Range).trim() !== "") return String(row.Range).trim();
  if (row.Category != null && String(row.Category).trim() !== "") return String(row.Category).trim();
  return "";
}

async function postDistributionPlotSamples(body) {
  const res = await api.post(DISTRIBUTION_PLOT_SAMPLES_PATH, body);
  return normalizeSampleRows(res?.data);
}

/**
 * CSV export: `plotKey` omitted; `listType` is `"all"` for full sample list download.
 */
export async function fetchQaqcDistributionPlotSamplesForCsvRows({
  testName = "ccetests",
  plotName,
  fluidType = "oil",
  listType = "all",
} = {}) {
  const pn = String(plotName ?? "").trim();
  if (!pn) return [];
  const rows = await postDistributionPlotSamples({
    testName,
    plotName: pn,
    fluidType,
    listType,
  });
  return rows.map(mapDistributionPlotApiRowToCsvFields).filter(Boolean);
}

/** Match backend plotKey style (e.g. "20-25") from chart category labels like "20 - 25". */
export function normalizePlotKeyForApi(label) {
  return String(label ?? "")
    .trim()
    .replace(/\u2013/g, "-")
    .replace(/\s*-\s*/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

export function histogramFailedSamplesRangeToken(numBins) {
  const n = Number(numBins);
  if (!Number.isFinite(n) || n < 2) return "histogram:10";
  return `histogram:${Math.round(n)}`;
}

/**
 * @param {{
 *   testName?: string,
 *   plotName: string,
 *   plotKey: string,
 *   fluidType?: string,
 *   listType?: string,
 * }} params
 * @returns {Promise<Array<{ id: string, fluidSampleID: string }>>}
 */
export async function fetchQaqcDistributionPlotSamples({
  testName = "ccetests",
  plotName,
  plotKey,
  fluidType = "oil",
  listType = "range",
} = {}) {
  const pn = String(plotName ?? "").trim();
  const pk = String(plotKey ?? "").trim();
  if (!pn || !pk) return [];
  const rows = await postDistributionPlotSamples({
    testName,
    plotName: pn,
    plotKey: normalizePlotKeyForApi(pk),
    fluidType,
    listType,
  });
  return rows.map(rowToChip).filter((c) => c.fluidSampleID || c.id);
}
